import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  mxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  ActiveReadingStatus,
  Formats,
  MeterReaderList,
  ReadingFormatList,
  ReadingStatusList,
  ReasonCodeList,
  UtilityService,
} from "./types";
import { PAGE_SIZE } from "@shared/utils/constants";

export const getMeterReadingFormat = async (
  remoteUtilityId: string,
  search: string,
): Promise<ReadingFormatList[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reading-format",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      if (search) {
        query.push("search_data", search);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ReadingFormatList[]>(response.data.result);
};

export const getUserUtilityServices = async (
  utilityId: string,
): Promise<UtilityService[]> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "auth",
    "utility",
    ["services"],
    (query) => {
      query.push("utility_id", utilityId);
    },
  );

  const response = await authApiClient.get(url);
  return transformJsonToObject<UtilityService[]>(response.data.result);
};

export const deleteManufacturere = async (id: number): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter/manufacturer/${id}`,
  );
  const response = await mxApiClient.delete(url);
  return response.data;
};

export const postMeterReadingFormat = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "meter-reading-format");
  const response = await mxApiClient.post(url, payload);
  return response?.data;
};

export const addWarehouse = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "warehouse");
  const response = await mxApiClient.post(url, payload);
  return response?.data;
};

export const updateWarehouse = async (id: string, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables("mx", "warehouse", [id]);
  const response = await mxApiClient.put(url, payload);
  return response?.data;
};

export const getMeterReadingFormatDetail = async (
  id: string,
  remoteUtilityId: string,
): Promise<Formats> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "meter-reading-format/detail",
    [id],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return transformJsonToObject<Formats>(response.data.result);
};

export const patchMeterReadingFormat = async (id: number, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "meter-reading-format",
    [id],
  );

  const response = await mxApiClient.patch(url, payload);
  return response.data;
};

export const deleteMeterReadingFormat = async (id: number) => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "meter-reading-format",
    [id],
  );

  const response = await mxApiClient.delete(url);
  return response.data;
};


export const getMeterReaderList = async ({
  page,
  remoteUtilityId,
  search,
}: {
  page: number;
  remoteUtilityId: string;
  search?: string;
}): Promise<MeterReaderList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reader",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", String(page));
      query.push("limit", 10);
      if (search) {
        query.push("search_data", search);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MeterReaderList>(response.data);
};

export const updateMeterReader = async (id: number, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables("mx", "meter-reader", [
    id,
  ]);
  const response = await mxApiClient.put(url, payload);
  return response.data;
};

export const getMeterReadingStatusList = async (
  remoteUtilityId: string,
  page: number,
  limit: any,
  search?: string,
): Promise<ReadingStatusList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reading-status",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_pagination_required", true);
      query.push("page", page);
      query.push("limit", limit);
      if (search) {
        query.push("search_data", search);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ReadingStatusList>(response.data);
};

export const updateMeterReadingStatus = async (id: string, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "meter-reading-status",
    [id],
  );

  const response = await mxApiClient.put(url, payload);
  return response?.data;
};

export const updateReasonCodeStatus = async (id: string, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables("mx", "reason-code", [
    id,
  ]);

  const response = await mxApiClient.put(url, payload);
  return response?.data;
};

export const postMeterReadingStatus = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "meter-reading-status");
  const response = await mxApiClient.post(url, payload);
  return response.data;
};

export const getMeterReasonCodesList = async (
  remoteUtilityId: string,
  page,
  limit: number,
  search?: string,
  meterReadingStatus?: string,
): Promise<ReasonCodeList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "reason-code",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_pagination_required", true);
      query.push("page", page);
      query.push("limit", limit);
      if (search) {
        query.push("search_data", search);
      }
      if(meterReadingStatus) query.push("meter_reading_status", meterReadingStatus);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ReasonCodeList>(response.data);
};

export const updateMeterReasonCodes = async (id: string, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables("mx", "reason-code", [
    id,
  ]);

  const response = await mxApiClient.put(url, payload);
  return response?.data;
};

export const postMeterReasonCodes = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "reason-code");
  const response = await mxApiClient.post(url, payload);
  return response.data;
};

export const getActiveMeterReadingStatuses = async (
  remoteUtilityId: string,
): Promise<ActiveReadingStatus[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reading-status",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_active", "True"); // Hardcoded
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ActiveReadingStatus[]>(response.data.result);
};

export const createMeterReader = async (formData: FormData): Promise<any> => {
  const url = ApiEndpoints.createUrl("mx", "meter-reader");
  const response = await mxApiClient.postForm(url, formData);
  return response.data;
};

export const getMeterReaderDetail = async (
  id: string,
  remoteUtilityId: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-reader/detail/${id}`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

//https://api-mx-dev.bynry.com/api/mx/meter-reader/assigned-routes/?meter_reader_id=177

export const getMeterReaderAssignedRoutes = async (
  id: string,
  remoteUtilityId: string,
  filters?: { cycle?: string; cycle_month?: string; route?: string },
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-reader/assigned-routes`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_reader_id", id);
      if (filters?.cycle) query.push("cycle", filters.cycle);
      if (filters?.cycle_month) query.push("cycle_month", filters.cycle_month);
      if (filters?.route) query.push("route", filters.route);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const getMeterReaderReadings = async (
  id: string,
  remoteUtilityId: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-reader/readings`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_reader_id", id);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const verifyMeterReaderEmail = async (email: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-reader/verify-email`,
  );
  const response = await mxApiClient.put(url, { email });
  return response.data;
};

export const updateMeterReaderAccess = async (
  id: string,
  payload: any,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `update-reader-access/${id}`,
  );
  const response = await mxApiClient.put(url, payload);
  return response.data;
};

export const getReadFormatFields = async (
  remoteUtilityId: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `choice/read_format_fields_v2`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result;
};

export const getCycleMonthList = async (meterReaderId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "cycle-month-list",
    (query) => {
      query.push("meter_reader_id", meterReaderId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const getCycleNameList = async (meterReaderId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "cycle-name-list",
    (query) => {
      query.push("meter_reader_id", meterReaderId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const getRouteNameList = async (meterReaderId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "route-name-list",
    (query) => {
      query.push("meter_reader_id", meterReaderId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const createResourceType = async (payload: any) => {
  const url = ApiEndpoints.createUrl("onboarding", "config-map/resource_json");
  const response = await onboardingApiClient.post(url, payload);
  return response.data;
};

export const createResourceSubType = async (payload: any) => {
  const url = ApiEndpoints.createUrl("onboarding", "config-map/resource_json");
  const response = await onboardingApiClient.post(url, payload);
  return response.data;
};



export const editResourceTypeSubtype = async (payload: any) => {
  const url = ApiEndpoints.createUrl("onboarding", "config-map/resource_json");
  const response = await onboardingApiClient.patch(url, payload);
  return response.data;
};

export const getReadMethods = async (): Promise<any> => {
  const url = ApiEndpoints.createUrl("mx", "choices/data_collection_method");
  const response = await mxApiClient.get(url);
  return response.data;
};

export const getWarehouses = async (
  remoteUtilityId: string,
  search: string,
  page: number = 1
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "warehouse",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_pagination_required", "true");
      query.push("page", String(page));
      query.push("page_size", String(PAGE_SIZE));
      if (search) {
        query.push("search_data", search);
      }
    }
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

export const getMeterManufacturers = async (params: { remote_utility_id: string; is_pagination_required?: boolean; page?: number; limit?: number; search_data?: string }): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    'mx',
    'meter/manufacturer',
    (qs) => {
      qs.push('remote_utility_id', params.remote_utility_id);
      if (params.is_pagination_required !== undefined) {
        qs.push('is_pagination_required', params.is_pagination_required);
      }
      if (params.search_data) {
        qs.push('search_data', params.search_data);
      }
      if (params.page) {
        qs.push('page', params.page);
      }
      if (params.limit) {
        qs.push('limit', params.limit);
      }
    }
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const getMeterModelDetail = async (params: { 
  remote_utility_id: string; 
  meter_model_id: string; 
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    'mx',
    'meter/model/detail',
    (qs) => {
      qs.push('remote_utility_id', params.remote_utility_id);
      qs.push('meter_model_id', params.meter_model_id);
    }
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const getMeterModels = async (params: {
  remote_utility_id: string,
  meter_type?: any,
  searchData?: any,
  manufacturer?: any,
  is_active?: any,
  page?: any,
  limit?: any
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    'mx',
    'meter/model',
    (qs) => {
      qs.push('remote_utility_id', params.remote_utility_id);

      if (params.meter_type && params.meter_type.toLowerCase?.() !== 'all') {
        qs.push("meter_type", params.meter_type);
      }

      if (params.searchData) {
        qs.push('search_data', params.searchData);
      }

      if (params.manufacturer && params.manufacturer.toLowerCase?.() !== 'all') {
        qs.push('manufacturer', params.manufacturer);
      }

      if (params.is_active) {
        qs.push('is_active', params.is_active);
      }

      if (params.page) {
        qs.push('page', params.page);
      }

      if (params.limit) {
        qs.push('limit', params.limit);
      }
    }
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

export const getReadingStatus = async (): Promise<any> => {
  const url = ApiEndpoints.createUrl(
    "mx",
    "choices/meter_meter_reading_status",
  );
  const response = await mxApiClient.get(url);
  return response.data;
};
